import React, { useMemo } from "react";
import {
  ANALYSIS_TYPE_CROSS,
  ANALYSIS_TYPE_LABELS,
  CROSS_COLUMN_HEADERS,
  CROSS_ROW_HEADERS,
} from "../constants";
import { computeCrossIntensities } from "../utils/cellIntensity";
import CrossMatrixCell from "./CrossMatrixCell";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE");
};

/**
 * The 3x3 Object × Variant cross matrix.
 *
 * Rows are the object importance class (A/B/C), columns the variant
 * importance class (A/B/C). The backend delivers the 9 cells in fixed
 * object-A→C × variant-A→C order, so they lay out positionally. Geometry,
 * label typography, vocabulary and the stability-driven cell color all mirror
 * ABCXYZMatrix — this is a third view of the same tile, not a separate
 * widget. Both axes being ABC axes is why each label names its own axis.
 *
 * Each cell's unit is the unique Object Group × Variant combination, not the
 * individual underlying objects (see abc_xyz.utils.build_cross_summary) —
 * objects excluded from that population are surfaced beneath the grid as two
 * distinct, named reasons (missing grouping value vs. outside the analysis
 * period), never as a single generic "not shown" count.
 */
const CrossMatrix = ({ summary, onCellClick, groupByAttribute = null }) => {
  const cells = summary?.matrix_cells;

  // Percentage-driven color intensity, normalized within each stability
  // family (the hue keeps its category meaning; the share sets the shade
  // strength) — exactly as the ABC/XYZ matrix does it.
  const intensities = useMemo(
    () => computeCrossIntensities(cells || []),
    [cells],
  );

  if (!cells || cells.length !== 9) return null;

  const objectType = summary.object_type;
  const outsideScope = summary.objects_outside_analysis_scope || 0;
  const missingGroup = summary.objects_missing_group_value || 0;
  const unclassifiedCombinations = summary.unclassified_combination_count || 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-sm font-semibold text-gray-800">
          {ANALYSIS_TYPE_LABELS[ANALYSIS_TYPE_CROSS]}
        </span>
        {/* Same two axis explanations the ABC/XYZ matrix gives, plus what the
            cross view adds: the combined stability rule. Decision-word
            meanings and cell-number meaning live on each cell's own hover
            instead of a header dump. */}
        <ABCXYZInfoTooltip
          sections={["cross", "abc", "xyz"]}
          title="Cross Matrix"
        />
      </div>

      {/* Capped width: at full tile width each cell runs past 400px to
          hold three short lines, which reads as stretched rather than
          dense. The cap keeps cells near the size of the other tiles'
          content blocks and leaves the grid left-aligned. */}
      <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] max-w-5xl flex-1 min-h-0">
        <div />

        <div className="grid grid-cols-3 gap-1.5 pb-2 pl-2">
          {["A", "B", "C"].map((abc) => (
            <div key={abc} className="flex items-center justify-center">
              <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                {abc}-Variant ({CROSS_COLUMN_HEADERS[abc]})
              </span>
            </div>
          ))}
        </div>

        {/* Object row labels */}
        <div className="grid grid-rows-3 gap-1.5 pr-3 pt-2 h-full">
          {["A", "B", "C"].map((abc) => (
            <div key={abc} className="flex items-center">
              <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                {abc}-Object ({CROSS_ROW_HEADERS[abc]})
              </span>
            </div>
          ))}
        </div>

        {/* Cells — track definition mirrors the label strips exactly. */}
        <div className="grid grid-cols-3 grid-rows-3 gap-1.5 pt-2 pl-2 h-full">
          {cells.map((cell) => (
            <CrossMatrixCell
              key={cell.cross_class}
              cell={cell}
              intensity={intensities[cell.cross_class] || 0}
              onCellClick={onCellClick}
              objectType={objectType}
            />
          ))}
        </div>
      </div>

      {/* Two structurally different exclusions, reported separately rather
          than as one generic "not shown" count — see
          abc_xyz.utils.build_cross_summary. Neither ever entered the Cross
          Matrix population, so neither is an "unclassified combination". */}
      {missingGroup > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          {formatNumber(missingGroup)}{" "}
          {objectType ? `"${objectType}"` : "object"} records excluded
          because {groupByAttribute ? `"${groupByAttribute}"` : "the grouping value"}{" "}
          is missing.
        </p>
      )}
      {outsideScope > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          {formatNumber(outsideScope)}{" "}
          {objectType ? `"${objectType}"` : "object"} records excluded because
          they fall outside the selected analysis period.
        </p>
      )}
      {unclassifiedCombinations > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          {formatNumber(unclassifiedCombinations)} combination
          {unclassifiedCombinations === 1 ? "" : "s"} could not be classified
          — this is unexpected and may indicate a data issue.
        </p>
      )}
    </div>
  );
};

export default CrossMatrix;
